import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Type, Lock, Share2 } from 'lucide-react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { supabase } from '../lib/supabase'
import { deriveCluesFromGrid } from '../utils/puzzleUtils'

export function CreatePage() {
  const { t } = useTranslation()

  // 5x5 Grid State
  const [grid, setGrid] = useState<string[][]>(
    Array(5).fill(null).map(() => Array(5).fill(''))
  )
  const [blocked, setBlocked] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<'fill' | 'block'>('fill')

  // Publishing State
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishStatus, setPublishStatus] = useState<{ success: boolean, message: string, shareUrl?: string } | null>(null)

  // Auto-generate numbering logic
  const [cellNumbers, setCellNumbers] = useState<number[][]>(
    Array(5).fill(null).map(() => Array(5).fill(0))
  )
  const [requiredClues, setRequiredClues] = useState<import('../utils/puzzleUtils').GridClueInfo[]>([])
  const [activeClueData, setActiveClueData] = useState<Record<string, string>>({})

  useEffect(() => {
    // Dynamically import to avoid circular dependency issues if any (though utils should be fine)
    // Actually, regular import is fine here as puzzleUtils doesn't depend on components.
    // Changing to utilize the already imported functions if I export them, but wait, I didn't add import stmt at top.

    // Let's use `deriveCluesFromGrid` from utils directly. I will add the import statement at the top.
    const { cellNumbers: nums, clueInfos } = deriveCluesFromGrid(5, blocked)
    setCellNumbers(nums)
    setRequiredClues(clueInfos)
  }, [blocked])

  // Sync activeClueData cleanup when clues disappear? 
  // Optional: keep old data to prevent loss during editing blocking toggles.

  const handleCellClick = (row: number, col: number) => {
    if (mode === 'block') {
      setBlocked(prev => {
        const next = new Set(prev)
        const key = `${row}-${col}`
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.add(key)
          // Also clear content if blocked
          setGrid(g => {
            const newG = [...g]
            newG[row] = [...newG[row]]
            newG[row][col] = ''
            return newG
          })
        }
        return next
      })
    }
  }

  const handleInput = (row: number, col: number, value: string) => {
    if (mode === 'block') return
    if (blocked.has(`${row}-${col}`)) return

    // Only allow letters
    if (value && !/^[a-zA-Z]$/.test(value)) return

    setGrid(prev => {
      const next = prev.map(r => [...r])
      next[row][col] = value.toUpperCase().slice(0, 1)
      return next
    })
  }

  const handleClueChange = (key: string, value: string) => {
    setActiveClueData(prev => ({ ...prev, [key]: value }))
  }

  const handlePublish = async () => {
    if (isPublishing) return
    setIsPublishing(true)
    setPublishStatus(null)

    // Build finalized clues object
    const finalizedClues = {
      across: [] as any[],
      down: [] as any[]
    }

    requiredClues.forEach(info => {
      const key = `${info.direction}-${info.number}`
      const text = activeClueData[key] || `${info.direction} ${info.number}` // Default fallback
      const clueObj = {
        number: info.number,
        clue: text,
        answer: getWordAnswer(info), // We need to extract the answer from the grid!
        row: info.row,
        col: info.col,
        startCell: [info.row, info.col]
      }
      finalizedClues[info.direction].push(clueObj)
    })

    const puzzleData = {
      title: `Custom Puzzle ${new Date().toLocaleDateString()}`,
      grid: {
        size: 5,
        cells: grid,
        blocked: Array.from(blocked).map(b => b.split('-').map(Number) as [number, number])
      },
      clues: finalizedClues,
      author: 'web-user',
      createdAt: new Date().toISOString()
    }

    try {
      if (supabase) {
        // Plan A: Save to Supabase
        const { data, error } = await supabase
          .from('user_puzzles')
          .insert([
            {
              grid_data: puzzleData,
              title: puzzleData.title,
              short_code: Math.random().toString(36).substring(2, 10) // Simple temp ID
            }
          ])
          .select()

        if (error) throw error

        setPublishStatus({
          success: true,
          message: t('builder.puzzle_saved'),
          shareUrl: 'https://blazegrids.netherfire.com/play/' + (data?.[0]?.short_code || 'demo')
        })
      } else {
        // Fallback or No Supabase
        const jsonString = JSON.stringify(puzzleData, null, 2)
        await navigator.clipboard.writeText(jsonString)
        setPublishStatus({
          success: true,
          message: t('builder.puzzle_copied'),
          shareUrl: undefined
        })
      }
    } catch (error) {
      console.error('Publish error:', error)
      setPublishStatus({
        success: false,
        message: t('builder.save_failed')
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const getWordAnswer = (info: import('../utils/puzzleUtils').GridClueInfo) => {
    let answer = ''
    if (info.direction === 'across') {
      for (let i = 0; i < info.length; i++) {
        answer += grid[info.row][info.col + i] || '?'
      }
    } else {
      for (let i = 0; i < info.length; i++) {
        answer += grid[info.row + i][info.col] || '?'
      }
    }
    return answer.toUpperCase()
  }

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-white">
      <Navbar showInstallButton={false} />

      <div className="flex-1 p-4 flex flex-col">
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col">
          <h1 className="text-3xl font-bold text-center mb-2">
            <span className="text-brand-orange">{t('builder.title')}</span>
          </h1>
          <p className="text-zinc-400 text-center mb-8 text-sm">
            {t('builder.subtitle')}
          </p>

          {/* Toolbar */}
          <div className="flex bg-zinc-900 rounded-xl p-1 mb-6 border border-zinc-800">
            <button
              onClick={() => setMode('fill')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${mode === 'fill'
                ? 'bg-brand-orange text-zinc-950 shadow-lg'
                : 'text-zinc-400 hover:bg-zinc-800'
                }`}
            >
              <Type size={18} />
              {t('builder.mode_fill')}
            </button>
            <button
              onClick={() => setMode('block')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${mode === 'block'
                ? 'bg-zinc-100 text-zinc-950 shadow-lg'
                : 'text-zinc-400 hover:bg-zinc-800'
                }`}
            >
              <Lock size={18} />
              {t('builder.mode_block')}
            </button>
          </div>

          {/* Grid Area */}
          <div className="aspect-square bg-zinc-900 rounded-xl p-4 mb-8 shadow-2xl border border-zinc-800 relative">
            <div
              className="grid gap-2 w-full h-full"
              style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}
            >
              {grid.map((row, r) => (
                row.map((cell, c) => {
                  const isBlocked = blocked.has(`${r}-${c}`)
                  const number = cellNumbers[r][c]

                  return (
                    <div
                      key={`${r}-${c}`}
                      onMouseDown={() => handleCellClick(r, c)}
                      className={`
                        relative rounded-lg flex items-center justify-center text-3xl font-bold
                        transition-all duration-200 cursor-pointer border-2
                        ${isBlocked
                          ? 'bg-zinc-950 border-zinc-800'
                          : 'bg-zinc-800 border-zinc-700 hover:border-zinc-500 hover:bg-zinc-700'}
                      `}
                    >
                      {/* Number */}
                      {!isBlocked && number > 0 && (
                        <span className="absolute top-1 left-1.5 text-[10px] text-zinc-500 font-mono leading-none z-10">
                          {number}
                        </span>
                      )}

                      {/* Input */}
                      {!isBlocked && (
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) => handleInput(r, c, e.target.value)}
                          className={`w-full h-full bg-transparent text-center text-white outline-none caret-brand-orange uppercase z-20 ${mode === 'block' ? 'pointer-events-none cursor-pointer' : ''} text-2xl p-0 m-0 border-0 rounded-none touch-manipulation`}
                          disabled={mode === 'block'}
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck="false"
                        />
                      )}
                    </div>
                  )
                })
              ))}
            </div>
          </div>

          {/* Action Panel */}
          <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 backdrop-blur-sm mb-8">
            <h3 className="text-zinc-500 font-bold uppercase text-xs tracking-wider mb-4">
              {t('builder.instructions_title')}
            </h3>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex gap-2">
                <span className="text-brand-orange">1.</span>
                {t('builder.step1')}
              </li>
              <li className="flex gap-2">
                <span className="text-brand-orange">2.</span>
                {t('builder.step2')}
              </li>
              {/* Clue Editor */}
              <li className="flex gap-2 items-start">
                <span className="text-brand-orange mt-1">3.</span>
                <div className="w-full">
                  <p className="mb-2 font-bold text-zinc-300">Clues</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    <div>
                      <h4 className="text-xs uppercase font-bold text-zinc-500 mb-2">Across</h4>
                      <div className="space-y-2">
                        {requiredClues.filter(c => c.direction === 'across').map(c => (
                          <div key={`across-${c.number}`} className="flex gap-2 items-center">
                            <span className="text-xs font-mono w-4 text-zinc-500">{c.number}.</span>
                            <input
                              type="text"
                              placeholder={`${c.length} letters`}
                              value={activeClueData[`across-${c.number}`] || ''}
                              onChange={(e) => handleClueChange(`across-${c.number}`, e.target.value)}
                              className="bg-zinc-800 border-zinc-700 rounded px-2 py-1 text-xs w-full focus:border-brand-orange outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs uppercase font-bold text-zinc-500 mb-2">Down</h4>
                      <div className="space-y-2">
                        {requiredClues.filter(c => c.direction === 'down').map(c => (
                          <div key={`down-${c.number}`} className="flex gap-2 items-center">
                            <span className="text-xs font-mono w-4 text-zinc-500">{c.number}.</span>
                            <input
                              type="text"
                              placeholder={`${c.length} letters`}
                              value={activeClueData[`down-${c.number}`] || ''}
                              onChange={(e) => handleClueChange(`down-${c.number}`, e.target.value)}
                              className="bg-zinc-800 border-zinc-700 rounded px-2 py-1 text-xs w-full focus:border-brand-orange outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <div className="mt-8">
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className="w-full btn btn-primary py-4 text-lg flex items-center justify-center gap-2 group"
              >
                {isPublishing ? (
                  <span>⏳ Publishing...</span>
                ) : (
                  <>
                    <Share2 size={20} className="group-hover:scale-110 transition-transform" />
                    {t('builder.publish')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Status Toast/Message */}
          {publishStatus && (
            <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 w-11/12 max-w-md rounded-xl shadow-2xl border animation-slide-up-fade ${publishStatus.success
              ? 'bg-emerald-950/90 border-emerald-800 text-emerald-100'
              : 'bg-red-950/90 border-red-800 text-red-100'
              }`}>
              <div className="p-4 flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-bold">{publishStatus.message}</p>
                  {publishStatus.shareUrl && (
                    <a href={publishStatus.shareUrl} className="text-xs underline mt-1 block opacity-80 break-all">
                      {publishStatus.shareUrl}
                    </a>
                  )}
                </div>
                <button onClick={() => setPublishStatus(null)} className="opacity-60 hover:opacity-100">
                  <ArrowLeft className="rotate-180" size={16} />
                </button>
              </div>

              {/* Social Share Buttons */}
              {publishStatus.success && publishStatus.shareUrl && (
                <div className="border-t border-emerald-800/50 p-3">
                  <p className="text-xs text-emerald-300 mb-2">{t('builder.share_puzzle')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const text = encodeURIComponent(t('builder.share_text'))
                        const url = encodeURIComponent(publishStatus.shareUrl!)
                        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank')
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
                    >
                      𝕏 Twitter
                    </button>
                    <button
                      onClick={() => {
                        const url = encodeURIComponent(publishStatus.shareUrl!)
                        const title = encodeURIComponent(t('builder.share_text'))
                        window.open(`https://reddit.com/submit?url=${url}&title=${title}`, '_blank')
                      }}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
                    >
                      📱 Reddit
                    </button>
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(publishStatus.shareUrl!)
                        alert('🔗 ' + t('game.copied'))
                      }}
                      className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
                    >
                      🔗 {t('game.copy_result')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}


        </div>
      </div>
      <Footer showAboutLink={false} />
    </div>
  )
}
